<template>
	<VDataGridV2
		:loader="dataLoader"
		:context-menu="PaymentRequestsService.contextMenu"
		sticky
		paginated
		entity-type="payment"
		@row:dblclick="openPaymentCard"
	>
		<VDataGridColumnV2
			id="id"
			width="80px"
			sortable
			sort-default-value="desc"
			filterable
			filter-send-name="idPattern"
			:filter-props="{ placeholder: 'Номер платежа или номер КСЗ', clearable: true }"
		>
			<template #label>
				<div>Заявка</div>
				<div class="description no-wrap">Номер КСЗ</div>
			</template>
			<template #default="{ row }">
				<div class="title">
					<VLink @click="openPaymentCard(row)">
						{{ $core.utils.string.formatWithDelimiter(row.id) }}
					</VLink>
				</div>
				<div class="description">{{ row.externalNumber }}</div>
				<VBadge v-if="row.isCanceled" class="warning">Отменён</VBadge>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="receiverName"
			label="Получатель"
			sortable
			width="130px"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				<div class="ellipsis" :title="row.receiver?.name">
					{{ row.receiver?.name }}
				</div>
				<div class="description ellipsis" :title="row.receiverCompany?.name">
					{{ row.receiverCompany?.name }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="plannedPaymentDate"
			width="120px"
			sortable
			filterable
			filter-type="date-range"
			:filter-props="{ clearable: true }"
		>
			<template #label>
				<div class="nowrap">План дата</div>
				<div>оплаты</div>
			</template>
			<template #default="{ row }">
				{{ row.plannedPaymentDate ? $core.utils.date.format(row.plannedPaymentDate) : '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="actualPaymentDate"
			width="120px"
			sortable
		>
			<template #label>
				<div class="nowrap">Факт дата</div>
				<div>оплаты</div>
			</template>
			<template #default="{ row }">
				{{ row.actualPaymentDate ? $core.utils.date.format(row.actualPaymentDate) : '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="isExported"
			label="Экспорт"
			width="120px"
			:content-class="(row) => ['ellipsis', row.exportState.modifier]"
			sortable
			filterable
			filter-type="select"
			:filter-props="{ options: PaymentRequestsService.exportStatusList, clearable: true }"
		>
			<template #default="{ row }">
				{{ row.isExportedToSWOracle === true ? 'Отправлено' : 'Ждёт отправки' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="actualSum"
			content-align="right"
			width="150px"
		>
			<template #label>
				<div class="nowrap">Сумма платежа</div>
				<div class="description nowrap">Фактическая сумма</div>
			</template>
			<template #default="{ row }">
				<div class="title">
					{{ row.paymentRequestSumFull?.format(row.paymentRequestCurrencyCode) }}
				</div>
				<div class="description">
					{{ row.actualPaymentSumFull?.format(row.actualPaymentCurrencyCode) || '--' }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="status"
			label="Статус"
			width="120px"
			:content-class="(row) => ['ellipsis', { 
				error: row.isCanceled === true && row.lastStatusText === 'Платеж отменен',
				success: row.isPaid === true 
			}]"
			filterable
			filter-type="select"
			filter-identity="isPaid,isCanceled,isProcessing"
			:filter-props="{ options: PaymentRequestsService.isPaidStatusList, clearable: true }"
			:filter-default-value="PaymentRequestsService.isPaidStatusList[1]"
		>
			<template #default="{ row }">
				{{ row.lastStatusText || '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="paymentPurposeText"
			label="Основание"
			filterable
			:filter-props="{ placeholder: 'Введите текст для поиска по основанию', clearable: true }"
			width="250px"
			content-class="ellipsis wrap"
		>
			<template #default="{ row }">
				<div :title="row.reasonText">{{ row.reasonText }}</div>
				<div v-if="row.commentary" :title="row.commentary" class="ellipsis">
					{{ row.commentary }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="priorityType"
			label="Приоритет"
			filterable
			filter-identity="*"
			filter-type="select"
			:filter-props="{ options: $core.dictionaries.paymentPriorityTypes, optionLabel: 'fullName', clearable: true }"
			width="150px"
			@edit="(row, anchor) => openPriorityPopup(row, anchor)"
		>
			<template #default="{ row }">
				{{ row.priorityType?.name || 'Не указан' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridActionBarV2>
			<VButton @click="openPaymentCard()">Создать платёж</VButton>
		</VDataGridActionBarV2>
	</VDataGridV2>
</template>

<script lang="ts">
import type BasePageFilter    from '@/domains/BasePageFilter/BasePageFilter';
import type PaymentRequest    from '@/domains/PaymentRequests/PaymentRequest';
import PaymentRequestsService from '@/domains/PaymentRequests/PaymentRequestsService';
import DataGridLoader         from '@/lib/components/VDataGridV2/DataGridLoader';

export default defineComponent({
	name: 'PaymentRequestsRegistry',

	props: {
		/**
		 * Класс страничного фильтра (localStorage). Передаётся, когда реестр
		 * монтируется внутри страницы с фильтром (воронка). Без него фильтр не подключается.
		 */
		filterClass: {
			type:    Function as unknown as () => typeof BasePageFilter,
			default: null,
		},

		params: {
			type:    Object as () => Parameters<typeof PaymentRequestsService.getListPaginated>[0],
			default: null,
		},
	},


	setup() {
		return {
			PaymentRequestsService,
		};
	},

	data() {
		return {
			dataLoader: new DataGridLoader(PaymentRequestsService.getListPaginated)
				.useReactiveSource(() => {
					return {
						...this.params,
					};
				}),
		};
	},

	methods: {
		// ==================== Действия ====================

		/** Без строки — создание нового платежа */
		async openPaymentCard(row: PaymentRequest | null = null): Promise<void> {
			if (await PaymentRequestsService.showCard(row?.id ?? null) === true) {
				await this.dataLoader.refresh();
			}
			else {
				// ничего не сохраняли
			}
		},

		// ==================== Priority popup ====================

		openPriorityPopup(row, anchor) {
			// если открыт предыдущий, то закрываем, если нет, то ничего не будет
			this.$core.dialogs.PaymentRequestChangePriorityPopup.hideIfOpen();

			this.$core.dialogs.PaymentRequestChangePriorityPopup.show({
				paymentRequest: row,
				target:         anchor,
			});
		},

		refresh(): void {
			this.dataLoader.refresh();
		},
	},
});
</script>